/*
 * GA4 dataLayer — begin_checkout + view_cart
 * ------------------------------------------------------------------
 * Pushes GA4 ecommerce events to window.dataLayer for GTM (GTM-5XTWSWR).
 *
 * Events: begin_checkout, view_cart (both live), add_to_cart (FLAG-GATED, OFF).
 *
 *   add_to_cart is implemented but disabled by default via
 *   window.ga4Config.emitAddToCart. The Stape Conversion Tracking app embed
 *   is enabled on this store and may already emit add_to_cart. Turning this
 *   on before confirming that in GA4 DebugView would double-count every add
 *   in GA4 and in Google Ads. To enable: set emitAddToCart to true in
 *   layout/theme.liquid.
 *
 *   view_item is NOT implemented here — same Stape overlap reason, and the
 *   theme already pushes an Analyzify product-detail event on PDP.
 *
 * Design constraints this file works around:
 *   - Checkout is Gokwik (an overlay), not native Shopify. snippets/gokwik.liquid
 *     is vendor-managed and carries "Do Not Touch This Line" markers, so we
 *     attach our own passive listeners instead of editing it.
 *   - Gokwik's MutationObserver re-binds checkout buttons roughly every 700ms,
 *     so per-element listeners would be re-added repeatedly. We use a single
 *     delegated capture-phase document listener instead.
 *   - The cart drawer is a custom element in custom-cart-drawer.js whose
 *     open() has several early-return recovery branches. Rather than patch it,
 *     we observe the resulting class change ("active") on the element.
 */
(function () {
  "use strict";

  window.dataLayer = window.dataLayer || [];

  var CURRENCY = (window.ga4Config && window.ga4Config.currency) || "INR";

  // Gokwik's own click handler guards with `clicked` and resets it on
  // modal_closed. We mirror that: one begin_checkout per opened modal.
  var checkoutInFlight = false;

  function toMajorUnits(cents) {
    var n = typeof cents === "number" ? cents : parseInt(cents, 10);
    if (isNaN(n)) return 0;
    return Math.round(n) / 100;
  }

  /* Shopify cart.js line -> GA4 item. Uses the modern GA4 field names
   * (item_id / item_name). The client workbook mixes legacy names (id/name)
   * for some events; if GTM variable mapping expects legacy, remap in GTM
   * rather than here, so this file stays spec-correct. */
  function lineToItem(line, index) {
    var item = {
      // Shape differs by source: /cart.js lines carry variant_id, whereas a
      // /cart/add.js response line carries the variant in `id`. Handle both.
      item_id: String(line.product_id || ""),
      item_name: line.product_title || line.title || "",
      item_brand: line.vendor || "",
      item_variant: line.variant_title || "",
      variant_id: String(line.variant_id || line.id || ""),
      sku: line.sku || "",
      price: toMajorUnits(line.final_price != null ? line.final_price : line.price),
      quantity: line.quantity || 1,
      index: index
    };

    // List attribution. NOTE: nothing in the theme sets these line properties
    // yet, so these fields are currently never populated — this is the
    // forward-compatible half of the work. To make item_list_name/id flow
    // through to checkout, the add-to-cart paths must attach
    // properties[_item_list_name] / [_item_list_id] at the point of add.
    // That is the separate "list attribution" task.
    var props = line.properties || {};
    if (props._item_list_name) item.item_list_name = props._item_list_name;
    if (props._item_list_id) item.item_list_id = props._item_list_id;

    return item;
  }

  function buildItems(cart) {
    var lines = (cart && cart.items) || [];
    return lines.map(lineToItem);
  }

  function fetchCart() {
    return fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    }).then(function (r) {
      if (!r || !r.ok) throw new Error("cart fetch failed");
      return r.json();
    });
  }

  function push(eventName, cart) {
    var items = buildItems(cart);
    if (!items.length) return; // never push an empty ecommerce event

    window.dataLayer.push({
      event: eventName,
      eventModel: {
        send_to: (window.ga4Config && window.ga4Config.sendTo) || undefined,
        value: toMajorUnits(cart.total_price),
        currency: cart.currency || CURRENCY,
        items: items
      }
    });
  }

  function pushFromCart(eventName) {
    return fetchCart()
      .then(function (cart) {
        push(eventName, cart);
        return cart;
      })
      .catch(function () {
        /* Analytics must never break the funnel. Swallow. */
      });
  }

  /* ---------------------------------------------------------------
   * begin_checkout
   * ---------------------------------------------------------------
   * Fires on the real Gokwik checkout triggers. The legacy Google Ads
   * handler in theme.liquid binds `.cart--button`, which no longer exists
   * in live markup (all occurrences are commented out) — that tracking has
   * been firing zero times. The live triggers are these two.
   *
   * NOT included: payment_type and coupon. Those are only known inside the
   * Gokwik overlay and cannot be read from theme code. They need Gokwik SDK
   * callbacks to populate.
   */
  var CHECKOUT_SELECTOR = "#gokwik-buy-now, .gokwik-checkout button";

  document.addEventListener(
    "click",
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== "function") return;

      var trigger = target.closest(CHECKOUT_SELECTOR);
      if (!trigger) return;
      if (trigger.disabled || trigger.classList.contains("disabled")) return;
      if (checkoutInFlight) return;

      checkoutInFlight = true;
      pushFromCart("begin_checkout");
    },
    true // capture: run before Gokwik's own handler / any stopPropagation
  );

  // Re-arm when the Gokwik modal closes, matching gokwik.liquid's reset.
  window.addEventListener("gokwikLoaded", function () {
    if (window.gokwikSdk && typeof window.gokwikSdk.on === "function") {
      window.gokwikSdk.on("modal_closed", function () {
        checkoutInFlight = false;
      });
    }
  });

  // Safety net: if the overlay never opened (SDK not ready, user bounced),
  // don't permanently suppress future begin_checkout events.
  window.addEventListener("pageshow", function () {
    checkoutInFlight = false;
  });

  /* ---------------------------------------------------------------
   * add_to_cart
   * ---------------------------------------------------------------
   * Fires ONLY on a server-confirmed /cart/add success — never on click.
   * The legacy Google Ads handlers in theme.liquid bind `.add-cart-btn`
   * clicks and fire even when the add fails, and are bound twice on the
   * same selector. This does not repeat that: no click binding at all.
   *
   * Why intercept the network instead of binding buttons: there are 9
   * distinct add-to-cart code paths in this theme (PDP, sticky bar, quick
   * add, bundles, BYOB, cart-drawer cross-sell, sibling PDP, grid stepper,
   * stock Dawn product-form) and `.add-cart-btn` appears in 20 files.
   * Wrapping fetch/XHR catches all of them, including third-party widgets.
   *
   * Deliberate exclusion: the free-gift engine (assets/cart-free-gift.js)
   * auto-adds promo gifts the user never chose. Those carry the
   * X-Cart-Free-Gift header and must NOT be reported as add_to_cart —
   * counting them would invent user intent that did not happen.
   *
   * NOTE ON fetch WRAPPING ORDER: theme.liquid also monkey-patches
   * window.fetch ~3s after load (Judge.me). Both wrappers delegate to the
   * previous window.fetch, so chaining is safe in either order. We tag our
   * wrapper so we never double-wrap on re-execution.
   */
  function addToCartEnabled() {
    return Boolean(window.ga4Config && window.ga4Config.emitAddToCart);
  }

  function isCartAddUrl(url) {
    if (!url) return false;
    try {
      return /\/cart\/add(\.js)?$/.test(new URL(url, window.location.origin).pathname);
    } catch (e) {
      return /\/cart\/add(\.js)?(\?|$)/.test(String(url));
    }
  }

  function headerValue(input, init, name) {
    try {
      var lower = name.toLowerCase();
      var h = (init && init.headers) || (input && input.headers);
      if (!h) return "";
      if (typeof h.get === "function") return h.get(name) || "";
      if (Array.isArray(h)) {
        for (var i = 0; i < h.length; i++) {
          if (h[i] && String(h[i][0]).toLowerCase() === lower) return String(h[i][1]);
        }
        return "";
      }
      for (var k in h) {
        if (String(k).toLowerCase() === lower) return String(h[k]);
      }
      return "";
    } catch (e) {
      return "";
    }
  }

  // Free-gift auto-adds are not user intent — never report them.
  function isFreeGiftRequest(input, init) {
    return headerValue(input, init, "X-Cart-Free-Gift") === "1";
  }

  /* The /cart/add.js response is the added line(s) — far more accurate than
   * diffing the cart, which races with concurrent adds. Shopify returns
   * either a single line object, or {items:[...]} for a batch add. */
  function itemsFromAddResponse(payload) {
    if (!payload) return [];
    var lines = Array.isArray(payload.items) ? payload.items : [payload];
    return lines
      .filter(function (l) {
        return l && (l.product_id || l.variant_id || l.id);
      })
      .map(function (l, i) {
        return lineToItem(l, i);
      });
  }

  function pushAddToCart(payload) {
    if (!addToCartEnabled()) return;

    var items = itemsFromAddResponse(payload);
    if (!items.length) return;

    var value = items.reduce(function (sum, it) {
      return sum + it.price * (it.quantity || 1);
    }, 0);

    window.dataLayer.push({
      event: "add_to_cart",
      eventModel: {
        send_to: (window.ga4Config && window.ga4Config.sendTo) || undefined,
        value: Math.round(value * 100) / 100,
        currency: CURRENCY,
        items: items
      }
    });
  }

  // --- fetch interception (covers 8 of 9 paths, incl. the grid stepper) ---
  if (window.fetch && !window.fetch.__ga4AtcWrapped) {
    var originalFetch = window.fetch;
    var ga4Fetch = function (input) {
      var init = arguments[1];
      var url = typeof input === "string" ? input : input && input.url ? input.url : "";
      var track = isCartAddUrl(url) && !isFreeGiftRequest(input, init);

      var promise = originalFetch.apply(this, arguments);
      if (!track) return promise;

      promise
        .then(function (response) {
          if (!response || !response.ok) return; // failed add => no event
          // clone(): never consume the body the caller is waiting on.
          return response
            .clone()
            .json()
            .then(pushAddToCart)
            .catch(function () {});
        })
        .catch(function () {});

      return promise;
    };
    ga4Fetch.__ga4AtcWrapped = true;
    window.fetch = ga4Fetch;
  }

  // --- XHR interception (older jQuery-based add paths) ---
  if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__ga4AtcWrapped) {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__ga4IsAdd = isCartAddUrl(url);
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (this.__ga4IsAdd) {
        this.addEventListener("load", function () {
          if (this.status < 200 || this.status >= 300) return;
          try {
            pushAddToCart(JSON.parse(this.responseText));
          } catch (e) {
            /* non-JSON response (native form post) — ignore */
          }
        });
      }
      return originalSend.apply(this, arguments);
    };

    XMLHttpRequest.prototype.__ga4AtcWrapped = true;
  }

  /* ---------------------------------------------------------------
   * view_cart
   * ---------------------------------------------------------------
   * Two surfaces: the slide-out drawer, and the standalone /cart page.
   */

  // (a) Drawer. custom-cart-drawer.js toggles the "active" class on the
  // <cart-drawer> element in open()/close(). Observing the attribute covers
  // every entry point (header icon, post-add auto-open, programmatic
  // openCartDrawer()) without patching open()'s early-return branches.
  function watchDrawer(drawer) {
    if (!drawer || drawer.__ga4CartWatched) return;
    drawer.__ga4CartWatched = true;

    var wasActive = drawer.classList.contains("active");

    var observer = new MutationObserver(function () {
      var isActive = drawer.classList.contains("active");
      if (isActive === wasActive) return;
      wasActive = isActive;
      if (isActive) pushFromCart("view_cart");
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ["class"] });

    if (wasActive) pushFromCart("view_cart");
  }

  function initDrawer() {
    var drawer = document.querySelector("cart-drawer");
    if (drawer) {
      watchDrawer(drawer);
      return;
    }

    // The drawer element may be injected late (section re-render). Watch for it
    // once, then disconnect.
    var bodyObserver = new MutationObserver(function () {
      var found = document.querySelector("cart-drawer");
      if (found) {
        bodyObserver.disconnect();
        watchDrawer(found);
      }
    });
    bodyObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  // (b) Standalone /cart page load.
  function initCartPage() {
    var template = (window.ga4Config && window.ga4Config.template) || "";
    if (template !== "cart") return;
    pushFromCart("view_cart");
  }

  function init() {
    initDrawer();
    initCartPage();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
